import { useEffect, useRef, useState } from 'react';
import { useCart } from '@/hooks/useCart';
import type { Product, ShoppableVideo } from '@/types';
import api from '@/services/api';
import { apiProductToProduct } from '@/services/adapters';

interface VideoShoppingModalProps {
  video: ShoppableVideo;
  isOpen: boolean;
  onClose: () => void;
}

export default function VideoShoppingModal({ video, isOpen, onClose }: VideoShoppingModalProps) {
  const { addToCart } = useCart();
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (isOpen && video.productId) {
      setLoading(true);
      api.get(`/products/${video.productId}`)
        .then(res => {
          const p = res.data?.product || res.data;
          setProduct(p ? apiProductToProduct(Array.isArray(p) ? p[0] : p) : null);
        })
        .catch(() => setProduct(null))
        .finally(() => setLoading(false));
      setQuantity(1);
      setAdded(false);
      setIsPlaying(false);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen, video?.productId]);

  const handleAddToCart = () => {
    if (!product) return;
    addToCart(product, quantity);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleBuyNow = () => {
    if (!product) return;
    addToCart(product, quantity);
    onClose();
  };

  const togglePlay = () => {
    if (isPlaying) {
      videoRef.current?.pause();
      setIsPlaying(false);
    } else {
      setIsPlaying(true);
    }
  };

  useEffect(() => {
    if (isPlaying && videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
  }, [isPlaying]);

  if (!isOpen || !video) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-0 md:p-6">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 flex flex-col md:flex-row w-full h-full md:h-auto md:max-h-[90vh] md:max-w-5xl md:rounded-2xl overflow-hidden bg-cream-50 shadow-2xl">
        <div className="relative flex-shrink-0 w-full md:w-[360px] lg:w-[400px] aspect-[9/16] md:aspect-[9/16] bg-charcoal-900 overflow-hidden">
          {isPlaying && video.videoUrl ? (
            <video ref={videoRef} src={video.videoUrl} className="w-full h-full object-cover" controls playsInline />
          ) : (
            <>
              <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
              <button onClick={togglePlay} className="absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity hover:bg-black/40">
                <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center shadow-lg hover:scale-105 transition-transform">
                  <i className="ri-play-fill text-2xl text-charcoal-900" />
                </div>
              </button>
            </>
          )}
          <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between bg-gradient-to-b from-black/50 to-transparent">
            <span className="text-white text-xs font-medium bg-white/20 backdrop-blur-sm px-2.5 py-1 rounded-full">{video.duration}</span>
            <span className="text-white text-xs flex items-center gap-1"><i className="ri-eye-line" />{video.views}</span>
          </div>
          <button onClick={onClose} className="absolute top-4 left-4 md:hidden w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center text-white"><i className="ri-close-line text-lg" /></button>
        </div>

        <div className="flex-1 flex flex-col overflow-y-auto">
          <div className="hidden md:flex items-center justify-between p-6 border-b border-cream-300">
            <span className="text-xs font-medium text-blush-200 bg-blush-100/50 px-3 py-1 rounded-full">{video.label}</span>
            <button onClick={onClose} className="w-9 h-9 rounded-full bg-cream-200 flex items-center justify-center text-charcoal-600 hover:bg-cream-300 transition-colors"><i className="ri-close-line text-lg" /></button>
          </div>

          {loading ? (
            <div className="p-8 flex items-center justify-center flex-1"><div className="animate-pulse w-12 h-12 bg-cream-200 rounded-full" /></div>
          ) : product ? (
            <div className="p-5 md:p-8 space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-20 h-20 rounded-xl overflow-hidden bg-white flex-shrink-0 border border-cream-300"><img src={product.image} alt={product.name} className="w-full h-full object-cover" /></div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-bold text-charcoal-900 leading-snug">{product.name}</h3>
                  <p className="text-sm text-charcoal-500 mt-1 line-clamp-2">{product.description}</p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-lg font-bold text-charcoal-900">{product.price} <span className="text-sm font-normal">شيكل</span></span>
                    {product.oldPrice && <span className="text-sm text-charcoal-400 line-through">{product.oldPrice} شيكل</span>}
                  </div>
                </div>
              </div>

{/* Rating hidden */}
              {/*
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-0.5">{Array.from({ length: 5 }).map((_, i) => (<i key={i} className={`w-4 h-4 flex items-center justify-center text-sm ${i < Math.floor(product.rating) ? 'ri-star-fill text-amber-400' : 'ri-star-line text-cream-400'}`} />))}</div>
                <span className="text-sm text-charcoal-500">{product.rating} ({product.reviewCount} تقييم)</span>
              </div>
              */}

              <div>
                <label className="text-sm font-medium text-charcoal-700 mb-2 block">الكمية</label>
                <div className="inline-flex items-center border border-cream-300 rounded-xl bg-white overflow-hidden">
                  <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-10 h-10 flex items-center justify-center text-charcoal-600 hover:bg-cream-100"><i className="ri-subtract-line" /></button>
                  <span className="w-12 text-center text-sm font-semibold text-charcoal-900">{product.inStock ? quantity : 0}</span>
                  <button onClick={() => product.inStock && setQuantity(Math.min(product.stockQuantity ?? 99, quantity + 1))} className="w-10 h-10 flex items-center justify-center text-charcoal-600 hover:bg-cream-100"><i className="ri-add-line" /></button>
                </div>
              </div>

              <div className="space-y-3 pt-2">
                <button onClick={handleAddToCart} disabled={added} className={`w-full py-3.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-2 ${added ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-charcoal-900 text-white hover:bg-charcoal-800'}`}>
                  {added ? (<><i className="ri-check-line" />تمت الإضافة إلى السلة</>) : (<><i className="ri-shopping-bag-line" />أضيفي إلى السلة</>)}
                </button>
                <button onClick={handleBuyNow} className="w-full py-3.5 rounded-xl text-sm font-semibold border-2 border-charcoal-900 text-charcoal-900 hover:bg-charcoal-900 hover:text-white transition-all flex items-center justify-center gap-2">شراء سريع</button>
              </div>

              {product.benefits.length > 0 && (
                <div className="border-t border-cream-300 pt-5">
                  <h4 className="text-sm font-semibold text-charcoal-700 mb-3">مميزات المنتج</h4>
                  <div className="grid grid-cols-2 gap-2">{product.benefits.slice(0, 4).map(benefit => (<div key={benefit} className="flex items-center gap-2"><i className="ri-checkbox-circle-line text-green-500 text-sm" /><span className="text-xs text-charcoal-600">{benefit}</span></div>))}</div>
                </div>
              )}

              <div className="border-t border-cream-300 pt-5">
                <h4 className="text-sm font-semibold text-charcoal-700 mb-2">طريقة الاستخدام</h4>
                <p className="text-xs text-charcoal-500 leading-relaxed line-clamp-3">{product.howToUse}</p>
              </div>
            </div>
          ) : (
            <div className="p-8 text-center text-charcoal-500"><i className="ri-error-warning-line text-3xl mb-3 block" />المنتج غير متوفر</div>
          )}
        </div>

        {/* Mobile Fixed Action Bar */}
        {product && (
          <div className="md:hidden fixed bottom-0 left-0 right-0 z-[110] bg-white border-t border-cream-300 p-3 flex items-center gap-3 shadow-lg">
            <span className="text-sm font-bold text-charcoal-900 whitespace-nowrap">{product.price} شيكل</span>
            <button onClick={handleAddToCart} disabled={added || !product.inStock} className={`flex-1 py-2.5 rounded-xl text-xs font-semibold transition-all flex items-center justify-center gap-1 ${!product.inStock ? 'bg-cream-200 text-charcoal-400 cursor-not-allowed' : added ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-charcoal-900 text-white'}`}>
              {!product.inStock ? 'غير متوفر' : added ? 'تمت الإضافة ✓' : 'أضف للسلة'}
            </button>
            <button onClick={handleBuyNow} disabled={!product.inStock} className={`flex-1 py-2.5 rounded-xl text-xs font-semibold border-2 transition-all ${!product.inStock ? 'border-cream-200 text-charcoal-300 cursor-not-allowed' : 'border-charcoal-900 text-charcoal-900'}`}>
              شراء سريع
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
